import { ReactNode, useState } from 'react';
import toast from 'react-hot-toast';
import { BilledInBadge } from '@/components/ads/BillingCurrency';
import { refreshTiktokAccountCurrency, useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { tiktokService } from '@/services/tiktok.service';
import { NavLink } from 'react-router-dom';
import { ArrowUpRight, BarChart3, LayoutDashboard, Megaphone, ShieldCheck } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { TikTokGlyph } from './workspace/workspaceShared';
import './tiktok.css';

export function TiktokShell({ section, title, description, actions, children }: {
  section: string; title: string; description: string; actions?: ReactNode; children: ReactNode;
}) {
  const { account, currency } = useTiktokAccountCurrency();
  const [refreshing, setRefreshing] = useState(false);
  // TikTok sets the billing currency; ask TikTok again instead of guessing.
  const refreshCurrency = async () => {
    if (!account) return;
    setRefreshing(true);
    try {
      const r = await tiktokService.refreshInfo(account.accountUid);
      await refreshTiktokAccountCurrency();
      toast.success(r.currency ? `TikTok account bills in ${r.currency}` : 'TikTok didn’t report a currency yet');
    } catch { /* interceptor toasts */ } finally { setRefreshing(false); }
  };
  return <div className="tt-module min-h-full">
    <Topbar title={title} description={description} breadcrumb={`TikTok Ads / ${section}`} autoBack={false}
      back={<span className="tt-brand"><TikTokGlyph size={27} /></span>}
      right={<>{account && <BilledInBadge platform="tiktok" currency={currency} onRefresh={refreshCurrency} refreshing={refreshing} />}{actions}<a className="tt-external" href="https://ads.tiktok.com/" target="_blank" rel="noreferrer" aria-label="Open TikTok Ads Manager"><ArrowUpRight size={16} /></a></>} />
    <div className="tt-page">
      <nav className="tt-nav" aria-label="TikTok navigation">
        {[
          { label: 'Overview', to: '/tiktok', Icon: LayoutDashboard, end: true },
          { label: 'Campaigns', to: '/tiktok/campaigns', Icon: Megaphone, end: false },
          { label: 'Measurement', to: '/tiktok/measurement', Icon: BarChart3, end: false },
          { label: 'Account', to: '/tiktok/account', Icon: ShieldCheck, end: false },
        ].map(({ label, to, Icon, end }) => <NavLink key={to} to={to} end={end}
          className={({ isActive }) => `tt-nav-link${isActive ? ' is-active' : ''}`}><Icon size={16} />{label}</NavLink>)}
      </nav>
      <div className="tt-content">      {children}
      </div>
    </div>
  </div>;
}

export function TiktokNotice({ title, children, error = false }: { title: string; children: ReactNode; error?: boolean }) {
  return <div className={`tt-notice${error ? ' is-error' : ''}`} role={error ? 'alert' : 'status'}><strong>{title}</strong><div>{children}</div></div>;
}
